
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { ShieldCheck } from "lucide-react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Logo from "../components/common/logo";

import ServiceSelector from "../components/booking/serviceSelector";
import BookingCalendar from "../components/booking/bookingCalendar";
import BookingForm from "../components/booking/bookingForm";

import INFO from "../data/user";
import SEO from "../data/seo";


import "../components/booking/booking.css";

const STEPS = ["service", "datetime", "details", "confirmed"];

const Booking = () => {
	useEffect(() => { window.scrollTo(0, 0); }, []);

	const currentSEO = SEO.find((item) => item.page === "contact");

	const [step, setStep] = useState("service");
	const [selectedService, setSelectedService] = useState(null);
	const [selectedDate, setSelectedDate] = useState(null);
	const [selectedTime, setSelectedTime] = useState(null);
	const [bookingDetails, setBookingDetails] = useState(null);

	const handleServiceSelect = (service) => {
		setSelectedService(service);
		setStep("datetime");
	};

	const handleDateTimeSelect = (date, time) => {
		setSelectedDate(date);
		setSelectedTime(time);
		setStep("details");
	};

	// Called by BookingForm after a successful Firestore write.
	const handleFormSubmit = (formData, confirmationLinks) => {
		setBookingDetails({ ...formData, ...confirmationLinks });
		setStep("confirmed");
	};

	const handleStartOver = () => {
		setSelectedService(null);
		setSelectedDate(null);
		setSelectedTime(null);
		setBookingDetails(null);
		setStep("service");
	};

	const stepIndex = STEPS.indexOf(step);

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Book a Session | ${INFO.main.title}`}</title>
				<meta
					name="description"
					content={
						currentSEO
							? currentSEO.description
							: "Book a counselling, coaching or speaking session."
					}
				/>
				{currentSEO && (
					<meta name="keywords" content={currentSEO.keywords.join(", ")} />
				)}
			</Helmet>

			<div className="page-content">
				<NavBar active="booking" />
				<div className="content-wrapper">
					<div className="contact-logo-container">
						<div className="contact-logo">
							<Logo width={46} />
						</div>
					</div>

					<div className="booking-container">
						<h1 className="booking-page-title">Book a session with Jabu Zwane</h1>
						<div className="subtitle booking-subtitle">
							Choose a service, pick a time that works for you, and
							I'll confirm your booking by email.
						</div>

						{step !== "confirmed" && (
							<div
								className="booking-progress"
								role="progressbar"
								aria-label="Booking progress"
								aria-valuemin={1}
								aria-valuemax={3}
								aria-valuenow={stepIndex + 1}
							>
								{STEPS.slice(0, 3).map((s, index) => (
									<div
										key={s}
										className={[
											"booking-progress-dot",
											index <= stepIndex
												? "booking-progress-dot-active"
												: "",
										].join(" ").trim()}
									/>
								))}
							</div>
						)}

						<div className="booking-panel">
							<div className="booking-privacy-note" role="note">
								<ShieldCheck size={20} strokeWidth={2.2} aria-hidden="true" />
								<span>
									<strong>POPIA-aligned booking</strong>
									Your details are handled responsibly and only used to manage your enquiry and booking.
								</span>
							</div>
							{step === "service" && (
								<ServiceSelector onSelect={handleServiceSelect} />
							)}

							{step === "datetime" && selectedService && (
								<BookingCalendar
									service={selectedService}
									onSelect={handleDateTimeSelect}
									onBack={() => setStep("service")}
								/>
							)}

							{step === "details" &&
								selectedService && selectedDate && selectedTime && (
									<BookingForm
										service={selectedService}
										date={selectedDate}
										time={selectedTime}
										onBack={() => setStep("datetime")}
										onSubmit={handleFormSubmit}
									/>
								)}

							{step === "confirmed" && bookingDetails && selectedService && (
								<div className="booking-confirmation">
									<div className="booking-confirmation-icon">&#10003;</div>
									<h2 className="step-title">You're booked in</h2>
									<p className="step-subtitle">
										Thanks, {bookingDetails.name}. A confirmation has been
										sent to {bookingDetails.email}.
									</p>

									<div className="booking-summary-card">
										<div className="booking-summary-row">
											<span className="booking-summary-label">Service</span>
											<span className="booking-summary-value">
												{selectedService.title}
											</span>
										</div>
										<div className="booking-summary-row">
											<span className="booking-summary-label">When</span>
											<span className="booking-summary-value">
												{new Date(
													`${selectedDate}T00:00:00`
												).toLocaleDateString("en-ZA", {
													weekday: "long",
													day: "numeric",
													month: "long",
												})}{" "}
												at {selectedTime}
											</span>
										</div>
										<div className="booking-summary-row">
											<span className="booking-summary-label">Format</span>
											<span className="booking-summary-value">
												{selectedService.mode === "in-person" ? "🏢 In Person" : "💻 Virtual"}
											</span>
										</div>
									</div>
									{bookingDetails.meetingSetupError && (
										<p role="status">
											Your booking is confirmed, but we couldn’t create its Zoom link yet. Please contact us before your appointment.
										</p>
									)}
									{bookingDetails.calendarInviteUrl && (
										<a
											className="btn btn-primary"
											href={bookingDetails.calendarInviteUrl}
										>
											Add to calendar (.ics)
										</a>
									)}
									{bookingDetails.meetingUrl && (
										<a
											className="btn btn-primary"
											href={bookingDetails.meetingUrl}
											target="_blank"
											rel="noreferrer"
										>
											Join your Zoom meeting
										</a>
									)}

									<button
										type="button"
										className="btn btn-primary"
										onClick={handleStartOver}
									>
										Book another session
									</button>
								</div>
							)}
						</div>
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default Booking;